import { useState, useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { getStats } from "../lib/api";
import {
  ChatCircleDots, FileText, UploadSimple, Bug, Buildings, CheckCircle,
  Database, ListChecks, GitBranch, Star, Gear, Envelope, PaperPlaneTilt,
  Heartbeat, CaretDoubleLeft, CaretDoubleRight, Notebook, Stack, FileArrowDown, Trophy,
} from "@phosphor-icons/react";

// Admin navigation model. Route items use a plain path; the 7 AdminPanel
// tabs are URL-driven via `/admin?tab=<value>` so they live in the sidebar
// alongside the standalone-page links.
const NAV = [
  {
    id: "tools", label: null, items: [
      { label: "Agent Chat", to: "/admin?tab=agent", icon: ChatCircleDots, testid: "agent" },
      { label: "Crawlers", to: "/admin/crawlers", icon: Bug, testid: "crawlers" },
      { label: "Documents crawled", to: "/admin/crawled-docs", icon: FileArrowDown, testid: "crawled-docs" },
    ],
  },
  {
    id: "ingestion", label: "Ingestion", items: [
      { label: "PDF Single", to: "/admin?tab=ingest", icon: FileText, testid: "ingest" },
      { label: "Bulk", to: "/admin?tab=bulk", icon: UploadSimple, testid: "bulk" },
      { label: "Ingestion Diary", to: "/admin/ingestion-diary", icon: Notebook, testid: "ingestion-diary" },
    ],
  },
  {
    id: "data", label: "Data", items: [
      { label: "Raw Allotments", to: "/admin?tab=allotments", icon: Database, testid: "allotments" },
      { label: "Merit List", to: "/admin/merit-list", icon: Trophy, testid: "merit-list" },
      { label: "Manage Rounds", to: "/admin?tab=rounds", icon: ListChecks, testid: "rounds" },
      { label: "NMC Raw", to: "/admin/nmc-raw", icon: Bug, testid: "nmc-raw" },
    ],
  },
  {
    id: "colleges", label: "Colleges", items: [
      { label: "Master", to: "/admin/master", icon: GitBranch, testid: "master" },
      { label: "Manage Colleges", to: "/admin?tab=colleges", icon: Buildings, testid: "colleges" },
      { label: "Review Colleges", to: "/admin?tab=review-colleges", icon: CheckCircle, testid: "review-colleges" },
    ],
  },
  {
    id: "predictor", label: "Predictor Config", items: [
      { label: "Fee Matrix", to: "/admin/predictor-fees", icon: FileText, testid: "predictor-fees" },
      { label: "Open States", to: "/admin/open-states", icon: Star, testid: "open-states" },
      { label: "Config", to: "/admin/config", icon: Gear, testid: "config" },
    ],
  },
  {
    id: "growth", label: "Growth", items: [
      { label: "Leads", to: "/admin/leads", icon: Envelope, testid: "leads" },
      { label: "Subscribers", to: "/admin/subscribers", icon: PaperPlaneTilt, testid: "subscribers" },
    ],
  },
  {
    id: "dbops", label: "DB Ops", items: [
      { label: "Migrations", to: "/admin/migrations", icon: Stack, testid: "migrations" },
      { label: "Architecture", to: "/admin/architecture", icon: Database, testid: "architecture" },
    ],
  },
  {
    id: "monitoring", label: "Monitoring", items: [
      { label: "Data Health", to: "/admin/data-health", icon: Heartbeat, testid: "data-health" },
      { label: "Logs", to: "/admin/logs", icon: FileText, testid: "logs" },
    ],
  },
];

export default function AdminLayout({ children }) {
  const loc = useLocation();
  const [sp] = useSearchParams();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("adminSidebarCollapsed") === "1");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    getStats().then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const toggle = () => {
    setCollapsed((c) => {
      localStorage.setItem("adminSidebarCollapsed", c ? "0" : "1");
      return !c;
    });
  };

  const currentTab = loc.pathname === "/admin" ? (sp.get("tab") || "agent") : null;
  const isActive = (to) => {
    const [path, query] = to.split("?");
    if (query) {
      const t = new URLSearchParams(query).get("tab");
      return loc.pathname === path && currentTab === t;
    }
    // exact match, or child routes (e.g. /admin/leads/templates highlights Leads)
    return loc.pathname === to || loc.pathname.startsWith(to + "/");
  };

  return (
    <div className="flex w-full items-stretch" data-testid="admin-layout">
      <aside
        className={`${collapsed ? "w-16" : "w-1/5 min-w-[200px] max-w-[300px]"} shrink-0 border-r border-zinc-200 bg-[#FAFAFA] sticky top-0 self-start h-[calc(100vh-0px)] overflow-y-auto transition-[width] duration-200 scrollbar-hide`}
        data-testid="admin-sidebar"
      >
        {/* Header / collapse toggle + college stats */}
        <div className={`${collapsed ? "" : "pb-3"} px-3 pt-3 border-b border-zinc-200 sticky top-0 bg-[#FAFAFA] z-10`}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "justify-between"}`}>
            {!collapsed && (
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#52525B]">Admin</span>
            )}
            <button
              onClick={toggle}
              className="w-8 h-8 flex items-center justify-center rounded hover:bg-zinc-200 text-[#52525B] transition-colors"
              title={collapsed ? "Expand" : "Collapse"}
              data-testid="admin-sidebar-toggle"
            >
              {collapsed ? <CaretDoubleRight size={15} weight="bold" /> : <CaretDoubleLeft size={15} weight="bold" />}
            </button>
          </div>
          {!collapsed && stats && (
            <div className="mt-2 text-[11px] leading-snug" data-testid="admin-stats">
              <div className="font-bold text-[#09090B]">COLLEGES: {stats.total_colleges ?? "—"}</div>
              <div>
                <span className="text-[#00C04B] font-semibold">Govt/{stats.govt_colleges ?? "—"}</span>
                <span className="text-[#A1A1AA]">, </span>
                <span className="text-[#FF2A00] font-semibold">Private/{stats.private_colleges ?? "—"}</span>
                <span className="text-[#A1A1AA]">, </span>
                <span className="text-[#6043EF] font-semibold">Deemed/{stats.deemed_colleges ?? "—"}</span>
              </div>
            </div>
          )}
        </div>

        <nav className="py-2">
          {NAV.map((group, gi) => (
            <div key={group.id} className={gi > 0 ? "mt-1 pt-1 border-t border-zinc-100" : ""}>
              {!collapsed && group.label && (
                <div className="px-4 pt-2 pb-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">
                  {group.label}
                </div>
              )}
              {group.items.map((it) => {
                const Icon = it.icon;
                const active = isActive(it.to);
                return (
                  <Link
                    key={it.to}
                    to={it.to}
                    title={collapsed ? it.label : undefined}
                    className={`flex items-center ${collapsed ? "justify-center px-0" : "px-4 gap-2.5"} h-9 text-xs font-semibold transition-colors border-l-2 ${
                      active
                        ? "bg-white text-[#6043EF] border-[#6043EF]"
                        : "text-[#3f3f46] border-transparent hover:bg-white hover:text-[#09090B]"
                    }`}
                    data-testid={`admin-nav-${it.testid}`}
                  >
                    <Icon size={16} weight={active ? "fill" : "regular"} className="shrink-0" />
                    {!collapsed && <span className="truncate">{it.label}</span>}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      <main className="flex-1 min-w-0" data-testid="admin-content">
        {children}
      </main>
    </div>
  );
}
